'use client'

import { useEffect, useState } from 'react'
import { useRouter, useParams } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { formatDT, formatDate } from '@/lib/utils'
import { ArrowLeft, Edit2, Trash2, Plus, Minus, CheckCircle, AlertTriangle, Camera, X } from 'lucide-react'
import Link from 'next/link'

export default function ArticleDetailPage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()
  const { id } = useParams<{ id: string }>()

  const [article, setArticle] = useState<any>(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<any>({})
  const [showAjust, setShowAjust] = useState(false)
  const [ajustDir, setAjustDir] = useState<'entree'|'sortie'>('entree')
  const [ajustQty, setAjustQty] = useState('')
  const [ajustNote, setAjustNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')
  const [uploadingImg, setUploadingImg] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    load()
  }, [session, id])

  async function load() {
    const data = await api.getArticle(id)
    setArticle(data); setForm(data)
  }

  async function saveEdit() {
    setSaving(true)
    await api.updateArticle(id, form)
    setEditing(false); setSaving(false)
    showToast('Sauvegardé ✓'); load()
  }

  async function ajustStock() {
    if (!ajustQty) return
    setSaving(true)
    await api.ajustStock(id, { shop_id: session!.shop.id, user_id: session!.user.id, type: ajustDir, quantite: parseInt(ajustQty), note: ajustNote, current_stock: article.stock })
    setAjustQty(''); setAjustNote(''); setShowAjust(false); setSaving(false)
    showToast('Stock mis à jour ✓'); load()
  }

  async function deleteArticle() {
    if (!confirm('Supprimer cet article?')) return
    await api.deleteArticle(id)
    router.replace('/articles')
  }

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 2500) }

  async function uploadImage(file: File) {
    setUploadingImg(true)
    const fd = new FormData()
    fd.append('image', file)
    const res = await fetch(`/api/articles/${id}/image`, { method: 'POST', body: fd })
    const data = await res.json()
    setUploadingImg(false)
    if (data.image_url) { setArticle((a: any) => ({ ...a, image_url: data.image_url })); showToast('Image ajoutée ✓') }
  }

  async function removeImage() {
    await fetch(`/api/articles/${id}/image`, { method: 'DELETE' })
    setArticle((a: any) => ({ ...a, image_url: null }))
    showToast('Image supprimée ✓')
  }

  const isPatron = session?.user.role === 'patron'
  const stats = article?.stats

  if (!article) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  const rasmelTotal = article.ras_mel * ((article.stock || 0) + (stats?.total_vendu || 0))
  const rasmelRajaa = stats && (parseFloat(stats.total_vendu) * article.prix_vente >= rasmelTotal)

  return (
    <div className="min-h-screen pb-8">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/articles" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1 truncate">{article.name}</h1>
          {isPatron && (
            <div className="flex gap-2">
              <button onClick={() => setEditing(!editing)} className={`p-2 rounded-xl ${editing ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600'}`}>
                <Edit2 className="w-5 h-5" />
              </button>
              <button onClick={deleteArticle} className="p-2 text-red-400"><Trash2 className="w-5 h-5" /></button>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Stock card */}
        <div className={`rounded-3xl p-5 ${article.stock <= 0 ? 'bg-red-50 border-2 border-red-200' : article.stock < article.stock_alert ? 'bg-amber-50 border-2 border-amber-200' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-500 text-sm">Stock actuel</p>
              <p className={`text-4xl font-bold ${article.stock <= 0 ? 'text-red-600' : article.stock < article.stock_alert ? 'text-amber-600' : 'text-gray-900'}`}>
                {article.stock} <span className="text-xl text-gray-400">{article.unit}</span>
              </p>
            </div>
            {article.stock < article.stock_alert && <AlertTriangle className={`w-8 h-8 ${article.stock <= 0 ? 'text-red-400' : 'text-amber-400'}`} />}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setAjustDir('entree'); setShowAjust(true) }}
              className="flex-1 py-2.5 bg-green-500 text-white rounded-2xl font-semibold flex items-center justify-center gap-1 active:scale-95">
              <Plus className="w-4 h-4" /> Entrée
            </button>
            <button onClick={() => { setAjustDir('sortie'); setShowAjust(true) }}
              className="flex-1 py-2.5 bg-orange-500 text-white rounded-2xl font-semibold flex items-center justify-center gap-1 active:scale-95">
              <Minus className="w-4 h-4" /> Sortie
            </button>
          </div>
        </div>

        {/* Image */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {article.image_url ? (
            <div className="relative">
              <img src={article.image_url} alt={article.name} className="w-full h-52 object-cover" />
              <button onClick={removeImage}
                className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 active:scale-95">
                <X className="w-4 h-4" />
              </button>
              <label className="absolute bottom-2 right-2 bg-black/50 text-white rounded-full p-1.5 active:scale-95 cursor-pointer">
                <Camera className="w-4 h-4" />
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])} />
              </label>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-32 cursor-pointer text-gray-400 gap-2 active:bg-gray-50">
              {uploadingImg ? (
                <div className="w-7 h-7 border-3 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Camera className="w-8 h-8" />
                  <span className="text-sm font-medium">Ajouter une photo</span>
                </>
              )}
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0])} />
            </label>
          )}
        </div>

        {/* Ras mel tracker */}
        {isPatron && stats && parseFloat(stats.total_vendu) > 0 && (
          <div className={`rounded-2xl p-4 shadow-sm border ${rasmelRajaa ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'}`}>
            <div className="flex justify-between mb-2">
              <h3 className="font-bold text-gray-800">Récupération ras mel</h3>
              {rasmelRajaa && <span className="text-green-600 font-bold text-sm">✅ Rajaa!</span>}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center mt-2">
              <div><p className="text-sm font-bold text-gray-800">{stats.total_vendu}</p><p className="text-xs text-gray-400">Vendus</p></div>
              <div><p className="text-sm font-bold text-indigo-700">{formatDT(parseFloat(stats.ca))}</p><p className="text-xs text-gray-400">CA</p></div>
              <div><p className={`text-sm font-bold ${parseFloat(stats.benefice) >= 0 ? 'text-green-600' : 'text-red-500'}`}>+{formatDT(parseFloat(stats.benefice))}</p><p className="text-xs text-gray-400">Bénéfice</p></div>
            </div>
          </div>
        )}

        {/* Info / Edit */}
        {editing ? (
          <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
            <h3 className="font-bold text-gray-800">Modifier</h3>
            {['name','barcode'].map(f => (
              <div key={f}>
                <label className="text-sm font-semibold text-gray-700 mb-1 block capitalize">{f === 'name' ? 'Nom' : 'Barcode'}</label>
                <input value={form[f] || ''} onChange={e => setForm((p: any) => ({...p,[f]:e.target.value}))}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
              </div>
            ))}
            {(['ras_mel','prix_vente','prix_min'] as const).map(f => (
              <div key={f}>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">
                  {f === 'ras_mel' ? 'Ras mel' : f === 'prix_vente' ? 'Prix vente' : 'Prix minimum'}
                </label>
                <div className="relative">
                  <input type="number" inputMode="decimal" value={form[f] || ''} onChange={e => setForm((p: any) => ({...p,[f]:parseFloat(e.target.value)}))}
                    className="w-full px-4 py-3 pr-12 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
                </div>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setEditing(false)} className="flex-1 py-3 bg-gray-100 rounded-2xl font-semibold text-gray-700">Annuler</button>
              <button onClick={saveEdit} disabled={saving} className="flex-1 py-3 bg-indigo-600 text-white rounded-2xl font-semibold disabled:opacity-50">
                {saving ? '...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            {[
              { label:'Prix vente',   value: formatDT(article.prix_vente), show:true },
              { label:'Prix minimum', value: formatDT(article.prix_min),   show:true },
              { label:'Ras mel',      value: formatDT(article.ras_mel),    show:isPatron },
              { label:'Marge',        value: `+${article.ras_mel > 0 ? (((article.prix_vente - article.ras_mel) / article.ras_mel)*100).toFixed(1) : 0}%`, show:isPatron },
              { label:'Alerte stock', value: `< ${article.stock_alert} ${article.unit}`, show:true },
              { label:'Barcode',      value: article.barcode || '—',       show:true },
              { label:'Mis à jour',   value: formatDate(article.updated_at), show:true },
            ].filter(r => r.show).map((r, i, arr) => (
              <div key={r.label} className={`flex justify-between px-4 py-3 ${i < arr.length-1 ? 'border-b border-gray-50' : ''}`}>
                <span className="text-gray-500 text-sm">{r.label}</span>
                <span className="font-semibold text-gray-800">{r.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAjust && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAjust(false)} />
          <div className="relative w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{ajustDir === 'entree' ? '📥 Entrée stock' : '📤 Sortie stock'}</h2>
            <div className="space-y-3 mb-5">
              <div className="relative">
                <input type="number" inputMode="numeric" autoFocus value={ajustQty} onChange={e => setAjustQty(e.target.value)} placeholder="Quantité..."
                  className="w-full px-4 py-3.5 pr-16 text-xl font-bold rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">{article.unit}</span>
              </div>
              <input value={ajustNote} onChange={e => setAjustNote(e.target.value)} placeholder="Note (optionnel)..."
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
            </div>
            <button onClick={ajustStock} disabled={saving || !ajustQty}
              className={`w-full py-4 text-white rounded-2xl font-bold text-lg disabled:opacity-50 ${ajustDir === 'entree' ? 'bg-green-500' : 'bg-orange-500'}`}>
              {saving ? '...' : 'Confirmer ✓'}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-green-500 text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-2 font-semibold">
          <CheckCircle className="w-4 h-4" /> {toast}
        </div>
      )}
    </div>
  )
}
